import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface GpsAlertEvent {
  id: string;
  rule_id: string;
  device_id: string;
  company_id: string;
  alert_type: string;
  priority: 'high' | 'medium' | 'low';
  happened_at: string;
  payload: Record<string, any>;
  ack_by?: string | null;
  ack_at?: string | null;
  resolved?: boolean;
  alert_rules?: {
    name: string;
    alert_type: string;
  };
  devices?: {
    name: string;
    imei: string;
    vehicle_id?: string;
  };
  fleet_vehicles?: {
    plate: string;
    make?: string;
    model?: string;
  } | null;
}

export function useGpsAlertEvents(companyId?: string) {
  return useQuery({
    queryKey: ['gps-alert-events', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      
      const { data, error } = await (supabase as any)
        .from('fleet_gps.alert_events')
        .select(`
          *,
          alert_rules!inner (
            name,
            alert_type
          ),
          devices!inner (
            name,
            imei,
            vehicle_id
          )
        `)
        .eq('company_id', companyId)
        .order('happened_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching GPS alert events:', error);
        throw error;
      }
      
      // Buscar veículos para os devices que têm vehicle_id
      const deviceIds = data?.map((event: any) => event.devices?.vehicle_id).filter(Boolean) || [];
      
      if (deviceIds.length > 0) {
        const { data: vehicles } = await supabase
          .from('fleet_vehicles')
          .select('id, plate, make, model')
          .in('id', deviceIds);
        
        // Mapear veículos aos eventos
        return data?.map((event: any) => ({
          ...event,
          fleet_vehicles: vehicles?.find(v => v.id === event.devices?.vehicle_id) || null
        })) as GpsAlertEvent[] || [];
      }
      
      return (data as GpsAlertEvent[]) || [];
    },
    enabled: !!companyId,
    staleTime: 30000, // 30 segundos
    refetchInterval: 60000, // 1 minuto
  });
}
